import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { AuthService } from '../../features/auth/services/auth.service';
import { environment } from '../../../environments/environment';
import { TENANT_HEADER } from '../interceptors/tenant.interceptor';

/** Fuente citada por Maqui (chip con deep-link al detalle). */
export interface MaquiSource {
  tipo: string;        // CLIENTE, OBRA, EQUIPO, ESTADO_PAGO, FACTURA…
  folio: string;       // EP-2026-0042, CIN-001, RUT…
  label?: string;
  entityType: string;  // cliente, obra, equipo, estado-pago, factura, pantalla
  entityId?: number;
  /** Pantalla de una guía de uso (`entityType: 'pantalla'`), que no es el detalle de una entidad. Ej: `/clientes`. */
  ruta?: string;
}

/** Fila del resumen de una acción propuesta (confirm card). */
export interface MaquiResumenItem {
  label: string;
  valor: string;
}

/** Acción de escritura propuesta por Maqui, pendiente de confirmación. */
export interface MaquiAction {
  /** Id de la tarjeta: separa dos acciones iguales pedidas a propósito de la misma confirmada dos veces. */
  id?: string;
  tipo: string;
  titulo: string;
  resumen: MaquiResumenItem[];
  payload: unknown;
}

/** Resultado de confirmar una acción (éxito → success card; bloqueo → warn). */
export interface MaquiConfirmarResponse {
  exito: boolean;
  tipo: string;
  mensaje: string;
  numeroDocumento?: string;
  entityType?: string;
  entityId?: number;
}

/** Respuesta de Maqui a una pregunta. */
export interface MaquiChatResponse {
  conversacionId: number;
  texto: string;
  sources: MaquiSource[];
  /** Respuestas rápidas (quick replies) data-backed para una pregunta aclaratoria. */
  sugerencias?: string[];
  accionPropuesta?: MaquiAction | null;
  toolsCalled: string[];
  tokensIn: number;
  tokensOut: number;
}

/** Modo de disponibilidad de Maqui: OFF (oculto) / PROXIMAMENTE (teaser) / ON (chat operativo). */
export type MaquiMode = 'OFF' | 'PROXIMAMENTE' | 'ON';

/** Consultas al asistente usadas en el mes, contra el cupo del plan. */
export interface MaquiUso {
  /** Consultas que permite el plan al mes. 0 = el plan no incluye asistente. */
  cap: number;
  /** Consultas hechas en el mes calendario actual, en toda la empresa. */
  usados: number;
  porcentaje: number | null;
  alcanzado: boolean;
  /** Lo que costó el mes, en USD. */
  costoUsdMes?: number;
  /** Tope de gasto del mes en USD (0 = sin tope). */
  topeCostoUsd?: number;
  /** El gasto tocó el tope: Maqui se corta aunque queden consultas. */
  presupuestoAlcanzado?: boolean;
  /** Quiénes consumieron, de mayor a menor. Solo llega a quien administra la cuenta. */
  porUsuario?: Array<{ usuario: string; consultas: number }> | null;
}

/** Estado de disponibilidad de Maqui en la instancia. */
export interface MaquiEstado {
  /** Chat operativo (flag + API key). */
  enabled: boolean;
  /** Modo del launcher: OFF (oculto), PROXIMAMENTE (teaser), ON (chat). */
  mode: MaquiMode;
  /** Consultas del mes contra el cupo del plan (solo cuando ON). */
  uso?: MaquiUso | null;
}

/** Nudge proactivo: una señal de negocio real y accionable (o null si no hay). */
export interface MaquiNudge {
  tipo: string;
  texto: string;
  rutaAccion: string;
  ctaLabel: string;
}

/** Ítem del historial de conversaciones. */
export interface MaquiConversacionItem {
  id: number;
  titulo: string;
  updatedAt: string;
}

/** Mensaje al rehidratar una conversación del historial. */
export interface MaquiMensajeItem {
  rol: 'USER' | 'ASSISTANT';
  texto: string;
  tipo?: string;
  sources?: MaquiSource[];
  createdAt: string;
}

/** Callbacks del streaming SSE de Maqui. */
export interface MaquiStreamHandlers {
  onDelta: (texto: string) => void;
  onConsulting?: (tools: string[]) => void;
  onSources?: (sources: MaquiSource[]) => void;
  onSugerencias?: (sugerencias: string[]) => void;
  onAction?: (accion: MaquiAction) => void;
  onDone: (data: { conversacionId: number }) => void;
  /** Si la conversación ya existía o se creó antes del error, llega su id para seguir en ella. */
  onError: (mensaje: string, conversacionId?: number) => void;
}

const MSG_SIN_CONEXION = 'No se pudo conectar con el asistente.';

/**
 * El mensaje que trae una respuesta HTTP fallida del stream, o uno genérico si no trae ninguno legible. Acepta el
 * formato de {@code RestResponse} ({@code message}) y el del interceptor de features ({@code message} / {@code error}).
 */
export async function mensajeDeRespuestaFallida(resp: Response): Promise<string> {
  try {
    const cuerpo = await resp.json();
    const mensaje = cuerpo?.message ?? cuerpo?.mensaje;
    if (typeof mensaje === 'string' && mensaje.trim()) return mensaje;
  } catch {
    // Sin cuerpo JSON: queda el genérico.
  }
  if (resp.status === 401) return 'Tu sesión expiró. Vuelve a entrar.';
  if (resp.status === 429) return 'Hiciste muchas consultas seguidas. Espera un minuto y vuelve a preguntar.';
  return MSG_SIN_CONEXION;
}

/**
 * Cliente del asistente Maqui (issue #648). Consulta POST /api/v1/maqui/chat
 * (grounded, con citación de fuentes) y GET /api/v1/maqui/estado.
 */
@Injectable({ providedIn: 'root' })
export class MaquiService {
  private http = inject(HttpService);
  private auth = inject(AuthService);

  /** ¿Maqui está habilitado en esta instancia? (silencioso, no molesta con toasts.) */
  estado(): Observable<MaquiEstado> {
    return this.http.getSilent<MaquiEstado>('/maqui/estado').pipe(
      map(r => {
        const d = r?.data;
        return {
          enabled: d?.enabled ?? false,
          // Fallback si un backend viejo no manda `mode`: derivar de enabled.
          mode: (d?.mode ?? (d?.enabled ? 'ON' : 'OFF')) as MaquiMode,
          uso: d?.uso ?? null,
        };
      })
    );
  }

  /** Nudge proactivo del día (señal de negocio) o null si no hay nada que avisar. */
  getNudge(): Observable<MaquiNudge | null> {
    return this.http.getSilent<MaquiNudge>('/maqui/nudge').pipe(
      map(r => r?.data ?? null)
    );
  }

  /** Envía una pregunta; devuelve la respuesta grounded con fuentes. */
  preguntar(pregunta: string, conversacionId?: number | null): Observable<MaquiChatResponse> {
    return this.http.post<MaquiChatResponse>('/maqui/chat', {
      pregunta,
      conversacionId: conversacionId ?? null,
    }).pipe(map(r => r.data as MaquiChatResponse));
  }

  /** Lista las conversaciones del usuario (historial). */
  getConversaciones(): Observable<MaquiConversacionItem[]> {
    return this.http.getSilent<MaquiConversacionItem[]>('/maqui/conversaciones').pipe(
      map(r => r?.data ?? [])
    );
  }

  /** Rehidrata los mensajes de una conversación. */
  getConversacion(id: number): Observable<MaquiMensajeItem[]> {
    return this.http.getSilent<MaquiMensajeItem[]>(`/maqui/conversaciones/${id}`).pipe(
      map(r => r?.data ?? [])
    );
  }

  /**
   * Confirma y ejecuta una acción propuesta (ej. crear guía). Silencioso: la tarjeta de Maqui muestra el resultado y,
   * si la red falla, dice que se revise antes de repetir; un toast de «intenta nuevamente» encima la contradecía.
   */
  confirmar(accion: MaquiAction, conversacionId?: number | null): Observable<MaquiConfirmarResponse> {
    return this.http.postSilent<MaquiConfirmarResponse>('/maqui/confirmar', {
      accion,
      conversacionId: conversacionId ?? null,
    }).pipe(map(r => r.data as MaquiConfirmarResponse));
  }

  /**
   * Streaming SSE de la respuesta (token por token). Usa fetch + ReadableStream
   * (EventSource no permite POST con header de auth). Devuelve una función para
   * abortar el stream.
   */
  streamPreguntar(pregunta: string, conversacionId: number | null,
                  handlers: MaquiStreamHandlers): () => void {
    const controller = new AbortController();
    const token = this.auth.getToken();

    fetch(`${environment.apiUrl}/maqui/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        // Esta llamada usa fetch y no HttpClient, así que el tenantInterceptor
        // no la toca: el dominio del cliente hay que ponerlo a mano o el
        // backend no sabe a qué instancia pertenece la conversación.
        [TENANT_HEADER]: window.location.hostname,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ pregunta, conversacionId }),
      signal: controller.signal,
    }).then(async (resp) => {
      if (!resp.ok || !resp.body) {
        // El backend explica el rechazo en el cuerpo (cuenta sin Maqui, demasiadas consultas,
        // sesión vencida): mostrarlo dice qué hacer, el genérico no.
        handlers.onError(await mensajeDeRespuestaFallida(resp));
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Los eventos SSE se separan por línea en blanco.
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          this.dispatchSse(raw, handlers);
        }
      }
    }).catch((e) => {
      if (e?.name !== 'AbortError') handlers.onError('Se interrumpió la respuesta del asistente.');
    });

    return () => controller.abort();
  }

  /** Parsea un bloque de evento SSE (event: + data:) y despacha al handler. */
  private dispatchSse(raw: string, h: MaquiStreamHandlers): void {
    let evento = 'message';
    let dataStr = '';
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) evento = line.slice(6).trim();
      else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
    }
    if (!dataStr) return;
    let data: any;
    try { data = JSON.parse(dataStr); } catch { return; }

    switch (evento) {
      case 'delta': h.onDelta(data.text ?? ''); break;
      case 'consulting': h.onConsulting?.(data.tools ?? []); break;
      case 'sources': h.onSources?.(data.sources ?? []); break;
      case 'sugerencias': h.onSugerencias?.(data.sugerencias ?? []); break;
      case 'action': h.onAction?.(data.accion); break;
      case 'done': h.onDone(data); break;
      case 'error':
        if (data.conversacionId != null) h.onError(data.mensaje ?? 'Error del asistente.', data.conversacionId);
        else h.onError(data.mensaje ?? 'Error del asistente.');
        break;
    }
  }
}
